import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/feature/Logo";

/**
 * Social profiles. The client reported these icons "no hacen nada": they were
 * hardcoded `href="#"`, so they looked clickable and went nowhere. Each icon renders only
 * once its URL is filled in here, so a network we do not have stays hidden rather than
 * shipping a dead link. Leave a network empty (or delete the line) to hide its icon.
 */
const SOCIAL_LINKS: { label: string; icon: string; url: string }[] = [
  { label: "Instagram", icon: "ri-instagram-line", url: "https://www.instagram.com/bajappxperiences" },
  { label: "Facebook", icon: "ri-facebook-line", url: "https://www.facebook.com/share/192KaeKxBr/" },
  { label: "YouTube", icon: "ri-youtube-line", url: "https://www.youtube.com/@bajappxperiences" },
];

/**
 * Bulldogti logo for the company block below (client request B5). White variant of the
 * imagotipo, since this footer sits on `bg-ocean`. Empty means no logo is rendered.
 */
const BULLDOGTI_LOGO_SRC = "/brand/bulldogti.png";

export default function Footer() {
  const { t } = useTranslation("home");
  const socialLinks = SOCIAL_LINKS.filter((s) => s.url.trim() !== "");

  return (
    <footer className="bg-ocean text-white">
      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Logo size={48} withWordmark wordmarkClassName="font-display text-xl font-bold" />
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              {t("footer.desc")}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-4 mt-6">
                {socialLinks.map((s) => (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label={s.label}
                  >
                    <i className={s.icon} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">{t("footer.about")}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/nosotros" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.whoWeAre")}</Link></li>
              <li><Link to="/blog" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.blog")}</Link></li>
              <li><Link to="/proveedores" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.press")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">{t("footer.help")}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/ayuda" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.helpCenter")}</Link></li>
              <li><Link to="/ayuda/faq" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.faq")}</Link></li>
              <li><Link to="/politica-cancelacion" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.cancellationPolicy")}</Link></li>
              <li><Link to="/terminos" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.terms")}</Link></li>
              <li><Link to="/privacidad" className="text-white/70 text-sm hover:text-white transition-colors">{t("footer.privacy")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">{t("footer.contact")}</h4>
            <ul className="space-y-2.5">
              <li className="text-white/70 text-sm flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-xs" />
                </div>
                soporte@bulldogti.com
              </li>
              <li className="text-white/70 text-sm flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-xs" />
                </div>
                +52 612 257 0821
              </li>
              <li className="text-white/70 text-sm flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-xs" />
                </div>
                La Paz, B.C.S., México
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-xs">
              © 2026 BajApp Xperiences. {t("footer.rights")}.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-xs">{t("footer.payments")}:</span>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-white/10 rounded text-[10px] font-medium">VISA</span>
                <span className="px-2 py-1 bg-white/10 rounded text-[10px] font-medium">MC</span>
                <span className="px-2 py-1 bg-white/10 rounded text-[10px] font-medium">PayPal</span>
              </div>
            </div>
          </div>

          {BULLDOGTI_LOGO_SRC && (
            <div className="mt-6 flex justify-center sm:justify-start">
              <img src={BULLDOGTI_LOGO_SRC} alt="Bulldog TI" className="h-12 w-auto opacity-80" />
            </div>
          )}

          <div className="mt-6 text-[11px] leading-relaxed text-white/50 text-center sm:text-left">
            <span className="font-medium text-white/70">SERVICIOS ESPECIALIZADOS BULLDOG TI</span>
            <span className="mx-1.5">·</span>
            RFC: SEB2201264R8
            <span className="mx-1.5">·</span>
            Régimen Simplificado de Confianza
            <br className="hidden sm:inline" />
            <span className="hidden sm:inline mx-1.5">·</span>
            Calle Villa Luna s/n, Col. Villas de la Paz, C.P. 23089, La Paz, Baja California Sur, México
          </div>
        </div>
      </div>
    </footer>
  );
}